import { ScrollView, styled, SxProp, View } from 'dripsy';
import React, { useEffect } from 'react';
import { Image, Platform, Pressable } from 'react-native';
import { Icon } from '../icons/Icon';

import { TextBody } from '../typography/UniTypography';

const PressableView = styled(Pressable)();
const ImageView = styled(Image)();
interface DropdownMenuProps<T> {
  isOpen: boolean;
  multiple?: boolean;
  dropdownLayout: {
    left: number;
    top: number;
    width: number;
  };
  data: Array<{
    label: string;
    value: T;
    id?: string;
    iconUrl?: string;
  }>;
  value?: T | T[];
  onSelect: (value: T) => void;
  variant?: 'text' | 'icon';
  sx?: SxProp;
  dropDownItemSx?: (hovered?: boolean, selected?: boolean) => SxProp;
}

export const DropdownMenu = <T extends unknown>({
  data,
  isOpen,
  multiple,
  dropdownLayout,
  value,
  onSelect,
  sx,
  dropDownItemSx,
  variant = 'text'
}: DropdownMenuProps<T>) => {
  if (!isOpen) return null;

  const containerStyle: SxProp = { ...styles.container };
  if (dropdownLayout.width !== undefined) {
    containerStyle.width = dropdownLayout.width;
  }
  if (dropdownLayout.top !== undefined) {
    containerStyle.top = dropdownLayout.top;
  }
  if (dropdownLayout.left !== undefined) {
    containerStyle.left = dropdownLayout.left;
  }

  return (
    <ScrollView sx={{ ...containerStyle, ...sx }} showsVerticalScrollIndicator={false}>
      {data.map((datum, i) => {
        if (variant === 'text')
          return (
            <DropdownItem
              key={`${datum.value}-${i}`}
              label={datum.label}
              multiple={multiple}
              selected={Array.isArray(value) ? value.includes(datum.value) : datum.value === value}
              onSelect={() => onSelect(datum.value)}
              sx={dropDownItemSx}
            />
          );
        return (
          <DropdownItemIcon
            key={`${datum.value}-${i}`}
            label={datum.label}
            multiple={multiple}
            selected={Array.isArray(value) ? value.includes(datum.value) : datum.value === value}
            onSelect={() => onSelect(datum.value)}
            iconUrl={datum.iconUrl}
            sx={dropDownItemSx}
          />
        );
      })}
    </ScrollView>
  );
};

const DropdownItem: React.FC<{
  label: string;
  multiple?: boolean;
  selected?: boolean;
  onSelect: () => void;
  sx?: (hovered?: boolean, selected?: boolean) => SxProp;
}> = ({ label, multiple, selected, sx, onSelect }) => {
  return (
    <PressableView onPress={onSelect}>
      {({ hovered }) => {
        return (
          <View
            sx={{
              ...styles.dropdownItem,
              backgroundColor: selected ? '$backgroundDropdownActive' : hovered ? '$backgroundTheme' : '$backgroundPopup',
              ...(sx && sx(hovered, selected))
            }}>
            <TextBody>{label}</TextBody>
            {multiple && selected && (
              <View>
                <Icon name="checkBlue" />
              </View>
            )}
          </View>
        );
      }}
    </PressableView>
  );
};

const DropdownItemIcon: React.FC<{
  label: string;
  iconUrl?: string;
  multiple?: boolean;
  selected?: boolean;
  onSelect: () => void;
  sx?: (hovered?: boolean, selected?: boolean) => SxProp;
}> = ({ label, iconUrl, multiple, selected, sx, onSelect }) => {
  return (
    <PressableView onPress={onSelect}>
      {({ hovered }) => {
        return (
          <View
            sx={{
              ...styles.dropdownItem,
              backgroundColor: selected ? '$backgroundDropdownActive' : hovered ? '$backgroundTheme' : '$backgroundPopup',
              ...(sx && sx(hovered, selected))
            }}>
            <View sx={styles.dropdownItemIcon}>
              <ImageView sx={{ height: '$5', width: '$5' }} source={{ uri: iconUrl }} />
              <TextBody>{label}</TextBody>
            </View>
            {multiple && selected && (
              <View>
                <Icon name="checkBlue" />
              </View>
            )}
          </View>
        );
      }}
    </PressableView>
  );
};

enum StyleName {
  container = 'container',
  dropdownItem = 'dropdownItem',
  dropdownItemIcon = 'dropdownItemIcon'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    display: 'block',
    position: 'absolute',
    left: 0,
    maxHeight: 250,
    flexDirection: 'column',
    my: '$2',
    py: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    zIndex: 9999,
    shadowColor: '#000000',
    shadowOffset: {
      width: Platform.OS === 'ios' ? 5 : 0,
      height: Platform.OS === 'ios' ? 5 : 2
    },
    shadowOpacity: Platform.OS === 'ios' ? 1.0 : 0.15,
    shadowRadius: 8,
    elevation: 5,
    borderWidth: Platform.OS === 'ios' ? 0.5 : 0,
    borderColor: Platform.OS === 'ios' ? '$borderButton' : 'transparent'
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '$11',
    px: '$2',
    py: '$3'
  },
  dropdownItemIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$2'
  }
};
